import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ClipboardList,
  MapPin,
  Pencil,
  Plus,
  Search,
  Trash2,
  Wrench,
} from "lucide-react";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";
import UIButton from "../components/UIButton";
import UIInput from "../components/UIInput";
import UILabel from "../components/UILabel";
import { notify } from "../utils/notify";

const initialForm = {
  title: "",
  projectId: "",
  projectName: "",
  clientName: "",
  location: "",
  date: "",
  startTime: "08:00",
  endTime: "16:00",
  type: "site_visit",
  status: "planned",
  notes: "",
};

const ui = {
  sq: {
    title: "Calendar / Terminplan",
    subtitle: "Planifiko takimet, vizitat në objekt, punët ditore dhe afatet e projekteve.",
    newEvent: "Termin i ri",
    editEvent: "Edito terminin",
    totalEvents: "Totali termineve",
    todayEvents: "Sot",
    upcomingEvents: "Në vazhdim",
    plannedProjects: "Projekte të planifikuara",
    searchPlaceholder: "Kërko termin, projekt ose lokacion",
    allTypes: "Të gjitha llojet",
    monthView: "Pamja mujore",
    upcomingList: "Terminet e ardhshme",
    noUpcoming: "Nuk ka termine të ardhshme",
    noUpcomingText: "Shto një termin të ri dhe ai do të shfaqet këtu.",
    dayAgenda: "Agjenda e ditës",
    noDayEvents: "Nuk ka termine për këtë ditë",
    noDayEventsText: "Kliko + Termin i ri për ta shtuar një të tillë.",
    titleLabel: "Titulli",
    projectLink: "Lidh projekt (opsionale)",
    noProject: "Pa projekt të lidhur",
    projectName: "Emri i projektit",
    clientName: "Klienti",
    location: "Lokacioni",
    date: "Data",
    startTime: "Ora e fillimit",
    endTime: "Ora e përfundimit",
    type: "Lloji",
    status: "Statusi",
    notes: "Shënime",
    notesPlaceholder: "Detaje për takimin, punën në objekt, materialet ose ekipin...",
    cancel: "Anulo",
    save: "Ruaj terminin",
    update: "Përditëso terminin",
    deleteConfirm: "A dëshiron ta fshish këtë termin?",
    saved: "Termini u ruajt me sukses.",
    updated: "Termini u përditësua me sukses.",
    deleted: "Termini u fshi me sukses.",
    loadError: "Gabim gjatë leximit të kalendarit.",
    saveError: "Gabim gjatë ruajtjes së terminit.",
    requiredTitle: "Shkruaj titullin e terminit.",
    today: "Sot",
    addForDay: "Shto për këtë ditë",
    eventDetails: "Detaje termini",
    allDay: "Gjithë dita",
    site_visit: "Vizitë në objekt",
    meeting: "Takim",
    work_day: "Punë në objekt",
    delivery: "Dorëzim materiali",
    reminder: "Kujtesë",
    planned: "Planned",
    done: "Done",
    cancelled: "Cancelled",
  },
  de: {
    title: "Kalender / Terminplan",
    subtitle: "Plane Termine, Baustellenbesuche, Tagesarbeiten und Projektfristen.",
    newEvent: "Neuer Termin",
    editEvent: "Termin bearbeiten",
    totalEvents: "Gesamttermine",
    todayEvents: "Heute",
    upcomingEvents: "Bevorstehend",
    plannedProjects: "Geplante Projekte",
    searchPlaceholder: "Termin, Projekt oder Ort suchen",
    allTypes: "Alle Typen",
    monthView: "Monatsansicht",
    upcomingList: "Kommende Termine",
    noUpcoming: "Keine kommenden Termine",
    noUpcomingText: "Erstelle einen neuen Termin und er erscheint hier.",
    dayAgenda: "Tagesagenda",
    noDayEvents: "Keine Termine an diesem Tag",
    noDayEventsText: "Klicke auf + Neuer Termin, um einen anzulegen.",
    titleLabel: "Titel",
    projectLink: "Projekt verknüpfen (optional)",
    noProject: "Kein verknüpftes Projekt",
    projectName: "Projektname",
    clientName: "Kunde",
    location: "Ort",
    date: "Datum",
    startTime: "Startzeit",
    endTime: "Endzeit",
    type: "Typ",
    status: "Status",
    notes: "Notizen",
    notesPlaceholder: "Details zum Termin, zur Baustelle, Materialien oder dem Team...",
    cancel: "Abbrechen",
    save: "Termin speichern",
    update: "Termin aktualisieren",
    deleteConfirm: "Diesen Termin wirklich löschen?",
    saved: "Termin wurde gespeichert.",
    updated: "Termin wurde aktualisiert.",
    deleted: "Termin wurde gelöscht.",
    loadError: "Fehler beim Laden des Kalenders.",
    saveError: "Fehler beim Speichern des Termins.",
    requiredTitle: "Bitte einen Titel eingeben.",
    today: "Heute",
    addForDay: "Für diesen Tag hinzufügen",
    eventDetails: "Termindetails",
    allDay: "Ganztägig",
    site_visit: "Baustellenbesuch",
    meeting: "Besprechung",
    work_day: "Arbeitstag",
    delivery: "Materiallieferung",
    reminder: "Erinnerung",
    planned: "Geplant",
    done: "Erledigt",
    cancelled: "Abgesagt",
  },
};

const eventTypes = ["site_visit", "meeting", "work_day", "delivery", "reminder"];
const eventStatuses = ["planned", "done", "cancelled"];
const weekdaySq = ["Hën", "Mar", "Mër", "Enj", "Pre", "Sht", "Die"];
const weekdayDe = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function pad(v) {
  return String(v).padStart(2, "0");
}

function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function normalizeEvent(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    title: data.title || "",
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    clientName: data.clientName || "",
    location: data.location || "",
    date: data.date || "",
    startTime: data.startTime || "",
    endTime: data.endTime || "",
    type: data.type || "site_visit",
    status: data.status || "planned",
    notes: data.notes || "",
    createdAt: data.createdAt || "",
    updatedAt: data.updatedAt || "",
  };
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}


function gridStart(date) {
  const d = startOfMonth(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function sameDay(a, b) {
  return a && b && a === b;
}

function buildMonthGrid(viewDate) {
  const start = gridStart(viewDate);
  return Array.from({ length: 42 }).map((_, index) => {
    const d = new Date(start);
    d.setDate(start.getDate() + index);
    return d;
  });
}

function formatMonthTitle(date, lang) {
  return new Intl.DateTimeFormat(lang === "de" ? "de-DE" : "sq-AL", {
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatDayLabel(iso, lang) {
  if (!iso) return "—";
  const date = new Date(`${iso}T00:00:00`);
  return new Intl.DateTimeFormat(lang === "de" ? "de-DE" : "sq-AL", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  }).format(date);
}

function sortEvents(list) {
  return [...list].sort((a, b) => {
    const left = `${a.date || ""} ${a.startTime || "00:00"}`;
    const right = `${b.date || ""} ${b.startTime || "00:00"}`;
    return left.localeCompare(right);
  });
}

function eventTypeClasses(type, theme) {
  const map = {
    site_visit: theme === "dark" ? "bg-blue-500/15 text-blue-300 border-blue-400/20" : "bg-blue-50 text-blue-700 border-blue-200",
    meeting: theme === "dark" ? "bg-violet-500/15 text-violet-300 border-violet-400/20" : "bg-violet-50 text-violet-700 border-violet-200",
    work_day: theme === "dark" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/20" : "bg-emerald-50 text-emerald-700 border-emerald-200",
    delivery: theme === "dark" ? "bg-amber-500/15 text-amber-300 border-amber-400/20" : "bg-amber-50 text-amber-700 border-amber-200",
    reminder: theme === "dark" ? "bg-slate-700 text-slate-200 border-slate-600" : "bg-slate-100 text-slate-700 border-slate-200",
  };
  return map[type] || map.site_visit;
}

function eventStatusClasses(status, theme) {
  const map = {
    planned: theme === "dark" ? "bg-slate-800 text-slate-200 border-slate-700" : "bg-slate-100 text-slate-700 border-slate-200",
    done: theme === "dark" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/20" : "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: theme === "dark" ? "bg-rose-500/15 text-rose-300 border-rose-400/20" : "bg-rose-50 text-rose-700 border-rose-200",
  };
  return map[status] || map.planned;
}

function toForm(event) {
  if (!event) return { ...initialForm };
  return {
    title: event.title || "",
    projectId: event.projectId || "",
    projectName: event.projectName || "",
    clientName: event.clientName || "",
    location: event.location || "",
    date: event.date || "",
    startTime: event.startTime || "08:00",
    endTime: event.endTime || "16:00",
    type: event.type || "site_visit",
    status: event.status || "planned",
    notes: event.notes || "",
  };
}

function buildPayload(form) {
  return {
    title: String(form.title || "").trim(),
    projectId: form.projectId || "",
    projectName: String(form.projectName || "").trim(),
    clientName: String(form.clientName || "").trim(),
    location: String(form.location || "").trim(),
    date: form.date || "",
    startTime: form.startTime || "",
    endTime: form.endTime || "",
    type: form.type || "site_visit",
    status: form.status || "planned",
    notes: String(form.notes || "").trim(),
    updatedAt: serverTimestamp(),
  };
}

function EventModal({ open, onClose, onSave, form, setForm, projects, editingId, text, theme }) {
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const shellClass =
    theme === "dark"
      ? "border-slate-800 bg-slate-950 text-slate-100"
      : "border-slate-200 bg-white text-slate-900";

  return createPortal(
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-slate-950/45 px-4 py-6 backdrop-blur-[3px] md:px-6 md:py-10">
      <div className="mx-auto flex min-h-full items-start justify-center md:items-center">
        <div className={`relative flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[30px] border shadow-[0_30px_80px_rgba(15,23,42,0.18)] md:max-h-[calc(100vh-5rem)] ${shellClass}`}>
          <div className={`sticky top-0 z-10 border-b px-4 py-4 md:px-6 ${theme === "dark" ? "border-slate-800 bg-slate-950/95" : "border-slate-200 bg-white/95"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-xl font-black md:text-2xl">{editingId ? text.editEvent : text.newEvent}</h3>
                <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition ${theme === "dark" ? "border-slate-800 bg-slate-900 hover:bg-slate-800" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}
              >
                ✕
              </button>
            </div>
          </div>

          <form onSubmit={onSave} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <UILabel>{text.titleLabel}</UILabel>
                <UIInput value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder={text.titleLabel} required />
              </div>

              <div>
                <UILabel>{text.projectLink}</UILabel>
                <select
                  value={form.projectId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedProject = projects.find((p) => p.id === selectedId);
                    setForm((prev) => ({
                      ...prev,
                      projectId: selectedId,
                      projectName: selectedProject?.name || prev.projectName || "",
                      clientName: selectedProject?.clientName || prev.clientName || "",
                      location: selectedProject?.address || prev.location || "",
                    }));
                  }}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  <option value="">{text.noProject}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name || "Project"}</option>
                  ))}
                </select>
              </div>

              <div>
                <UILabel>{text.projectName}</UILabel>
                <UIInput value={form.projectName} onChange={(e) => setForm((p) => ({ ...p, projectName: e.target.value }))} placeholder={text.projectName} />
              </div>

              <div>
                <UILabel>{text.clientName}</UILabel>
                <UIInput value={form.clientName} onChange={(e) => setForm((p) => ({ ...p, clientName: e.target.value }))} placeholder={text.clientName} />
              </div>

              <div>
                <UILabel>{text.location}</UILabel>
                <UIInput value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} placeholder={text.location} />
              </div>

              <div>
                <UILabel>{text.date}</UILabel>
                <UIInput type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
              </div>

              <div>
                <UILabel>{text.type}</UILabel>
                <select
                  value={form.type}
                  onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  {eventTypes.map((type) => <option key={type} value={type}>{text[type]}</option>)}
                </select>
              </div>

              <div>
                <UILabel>{text.startTime}</UILabel>
                <UIInput type="time" value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} />
              </div>

              <div>
                <UILabel>{text.endTime}</UILabel>
                <UIInput type="time" value={form.endTime} onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} />
              </div>

              <div className="md:col-span-2">
                <UILabel>{text.status}</UILabel>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  {eventStatuses.map((status) => <option key={status} value={status}>{text[status]}</option>)}
                </select>
              </div>

              <div className="md:col-span-2">
                <UILabel>{text.notes}</UILabel>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder={text.notesPlaceholder}
                  rows={4}
                  className={`w-full rounded-3xl border px-4 py-3 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500" : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400"}`}
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:justify-end">
              <UIButton type="button" variant="secondary" onClick={onClose}>{text.cancel}</UIButton>
              <UIButton type="submit">{editingId ? text.update : text.save}</UIButton>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Calendar({ lang = "sq" }) {
  const text = ui[lang] || ui.sq;
  const { theme, currentTheme } = usePanelTheme();

  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({ ...initialForm, date: todayIso() });

  const loadCalendarData = async () => {
    setLoading(true);
    try {
      const [eventsSnap, projectsSnap] = await Promise.all([
        getDocs(query(collection(db, "calendar_events"), orderBy("date", "asc"))),
        getDocs(query(collection(db, "projects"), orderBy("createdAt", "desc"))),
      ]);

      setEvents(sortEvents(eventsSnap.docs.map(normalizeEvent)));
      setProjects(
        projectsSnap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }))
      );
    } catch (error) {
      console.error(error);
      notify.error(text.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEvents = useMemo(() => {
    const needle = String(search || "").toLowerCase().trim();
    return events.filter((event) => {
      const haystack = [event.title, event.projectName, event.clientName, event.location].join(" ").toLowerCase();
      const matchesSearch = !needle || haystack.includes(needle);
      const matchesType = typeFilter === "all" ? true : event.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [events, search, typeFilter]);

  const eventsByDate = useMemo(() => {
    const map = {};
    filteredEvents.forEach((event) => {
      if (!event.date) return;
      if (!map[event.date]) map[event.date] = [];
      map[event.date].push(event);
    });
    Object.keys(map).forEach((key) => {
      map[key] = sortEvents(map[key]);
    });
    return map;
  }, [filteredEvents]);

  const monthDays = useMemo(() => buildMonthGrid(viewDate), [viewDate]);

  const upcoming = useMemo(() => {
    const today = todayIso();
    return filteredEvents.filter((event) => event.date >= today).slice(0, 8);
  }, [filteredEvents]);

  const selectedDayEvents = useMemo(() => eventsByDate[selectedDate] || [], [eventsByDate, selectedDate]);

  const stats = useMemo(() => {
    const today = todayIso();
    return {
      totalEvents: events.length,
      todayEvents: events.filter((event) => event.date === today).length,
      upcomingEvents: events.filter((event) => event.date >= today).length,
      plannedProjects: projects.filter((project) => (project.status || "") === "planned").length,
    };
  }, [events, projects]);

  const openCreateModal = (presetDate = selectedDate) => {
    setEditingEvent(null);
    setForm({ ...initialForm, date: presetDate || todayIso(), startTime: "08:00", endTime: "16:00" });
    setModalOpen(true);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setForm(toForm(event));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingEvent(null);
    setForm({ ...initialForm, date: selectedDate || todayIso() });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!String(form.title || "").trim()) {
      notify.error(text.requiredTitle);
      return;
    }

    try {
      const payload = buildPayload(form);
      if (editingEvent?.id) {
        await updateDoc(doc(db, "calendar_events", editingEvent.id), payload);
        notify.success(text.updated);
      } else {
        await addDoc(collection(db, "calendar_events"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        notify.success(text.saved);
      }
      closeModal();
      await loadCalendarData();
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    }
  };

  const handleDelete = async (eventId) => {
    if (!eventId) return;
    if (!window.confirm(text.deleteConfirm)) return;
    try {
      await deleteDoc(doc(db, "calendar_events", eventId));
      notify.success(text.deleted);
      await loadCalendarData();
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    }
  };

  const weekdayLabels = lang === "de" ? weekdayDe : weekdaySq;

  return (
    <PageShell>
      <SectionHeading
        eyebrow="BAUHELD PREMIUM UI"
        title={text.title}
        subtitle={text.subtitle}
        action={
          <UIButton onClick={() => openCreateModal()} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            {text.newEvent}
          </UIButton>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: text.totalEvents,
            value: stats.totalEvents,
            icon: CalendarDays,
            cardClass: theme === "dark" ? "border-blue-500/20 bg-blue-500/10" : "border-blue-200 bg-blue-50/70",
            iconClass: theme === "dark" ? "bg-blue-500/15 text-blue-300" : "bg-blue-100 text-blue-700",
          },
          {
            label: text.todayEvents,
            value: stats.todayEvents,
            icon: Clock3,
            cardClass: theme === "dark" ? "border-amber-500/20 bg-amber-500/10" : "border-amber-200 bg-amber-50/70",
            iconClass: theme === "dark" ? "bg-amber-500/15 text-amber-300" : "bg-amber-100 text-amber-700",
          },
          {
            label: text.upcomingEvents,
            value: stats.upcomingEvents,
            icon: ClipboardList,
            cardClass: theme === "dark" ? "border-violet-500/20 bg-violet-500/10" : "border-violet-200 bg-violet-50/70",
            iconClass: theme === "dark" ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-700",
          },
          {
            label: text.plannedProjects,
            value: stats.plannedProjects,
            icon: Wrench,
            cardClass: theme === "dark" ? "border-emerald-500/20 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50/70",
            iconClass: theme === "dark" ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700",
          },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <PanelCard key={stat.label} className={stat.cardClass}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={`text-xs sm:text-sm font-semibold ${currentTheme.muted}`}>{stat.label}</p>
                  <div className={`mt-2 text-3xl sm:text-4xl font-black tracking-tight ${currentTheme.text}`}>{stat.value}</div>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${stat.iconClass}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </PanelCard>
          );
        })}
      </div>

      <PanelCard>
        <div className="grid gap-3 lg:grid-cols-[1.35fr_220px_auto]">
          <div className="relative">
            <Search className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${currentTheme.muted}`} />
            <UIInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder={text.searchPlaceholder} className="pl-11" />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
          >
            <option value="all">{text.allTypes}</option>
            {eventTypes.map((type) => <option key={type} value={type}>{text[type]}</option>)}
          </select>

          <UIButton variant="secondary" onClick={() => setSelectedDate(todayIso())} className="w-full lg:w-auto">
            {text.today}
          </UIButton>
        </div>
      </PanelCard>

      <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
        <PanelCard className="overflow-hidden p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 md:px-6 md:py-5">
            <div>
              <div className={`text-sm font-semibold ${currentTheme.muted}`}>{text.monthView}</div>
              <h3 className={`mt-1 text-2xl font-black capitalize ${currentTheme.text}`}>{formatMonthTitle(viewDate, lang)}</h3>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition ${theme === "dark" ? "border-slate-800 bg-slate-900 text-slate-100 hover:bg-slate-800" : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition ${theme === "dark" ? "border-slate-800 bg-slate-900 text-slate-100 hover:bg-slate-800" : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px border-b px-2 pb-2 pt-3 md:px-3">
            {weekdayLabels.map((label) => (
              <div key={label} className={`px-2 py-2 text-center text-xs font-extrabold uppercase tracking-[0.18em] ${currentTheme.muted}`}>
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-px px-2 pb-2 md:px-3 md:pb-3">
            {monthDays.map((date) => {
              const iso = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
              const dayEvents = eventsByDate[iso] || [];
              const inCurrentMonth = date.getMonth() === viewDate.getMonth();
              const selected = sameDay(iso, selectedDate);
              const isToday = sameDay(iso, todayIso());
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => setSelectedDate(iso)}
                  className={`min-h-[108px] rounded-[22px] border p-2 text-left transition md:min-h-[118px] ${selected ? (theme === "dark" ? "border-blue-400/30 bg-blue-500/10" : "border-blue-200 bg-blue-50/80") : theme === "dark" ? "border-slate-800 bg-slate-950/40 hover:bg-slate-900" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-extrabold ${inCurrentMonth ? currentTheme.text : currentTheme.muted}`}>{date.getDate()}</span>
                    {isToday ? (
                      <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-white">
                        {text.today}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-2 space-y-1">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div key={event.id} className={`truncate rounded-xl border px-2 py-1 text-[11px] font-semibold ${eventTypeClasses(event.type, theme)}`}>
                        {event.startTime ? `${event.startTime} · ` : ""}{event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 ? (
                      <div className={`text-[11px] font-semibold ${currentTheme.muted}`}>+{dayEvents.length - 3}</div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </PanelCard>

        <div className="space-y-6">
          <PanelCard>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className={`text-xl font-black ${currentTheme.text}`}>{text.upcomingList}</h3>
                <p className={`mt-1 text-sm ${currentTheme.muted}`}>{text.subtitle}</p>
              </div>
              <UIButton variant="secondary" onClick={() => openCreateModal(selectedDate)}>
                <Plus className="mr-2 h-4 w-4" />
                {text.newEvent}
              </UIButton>
            </div>

            <div className="mt-5 space-y-3">
              {!loading && !upcoming.length ? (
                <div className={`rounded-[24px] border border-dashed p-5 text-center ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                  <div className="text-base font-bold">{text.noUpcoming}</div>
                  <div className="mt-1 text-sm">{text.noUpcomingText}</div>
                </div>
              ) : (
                upcoming.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => {
                      setSelectedDate(event.date || todayIso());
                      openEditModal(event);
                    }}
                    className={`w-full rounded-[24px] border p-4 text-left transition ${theme === "dark" ? "border-slate-800 bg-slate-950/40 hover:bg-slate-900" : "border-slate-200 bg-slate-50 hover:bg-slate-100/70"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-base font-bold">{event.title}</div>
                        <div className={`mt-1 text-sm ${currentTheme.muted}`}>{formatDayLabel(event.date, lang)}</div>
                      </div>
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${eventTypeClasses(event.type, theme)}`}>{text[event.type]}</span>
                    </div>
                    <div className={`mt-3 flex flex-wrap gap-3 text-sm ${currentTheme.muted}`}>
                      <span className="inline-flex items-center gap-1"><Clock3 className="h-4 w-4" />{event.startTime || text.allDay}{event.endTime ? ` - ${event.endTime}` : ""}</span>
                      {!!event.location && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{event.location}</span>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </PanelCard>

          <PanelCard>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className={`text-xl font-black ${currentTheme.text}`}>{text.dayAgenda}</h3>
                <p className={`mt-1 text-sm ${currentTheme.muted}`}>{formatDayLabel(selectedDate, lang)}</p>
              </div>
              <UIButton onClick={() => openCreateModal(selectedDate)}>
                <Plus className="mr-2 h-4 w-4" />
                {text.addForDay}
              </UIButton>
            </div>

            <div className="mt-5 space-y-3">
              {!selectedDayEvents.length ? (
                <div className={`rounded-[24px] border border-dashed p-5 text-center ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                  <div className="text-base font-bold">{text.noDayEvents}</div>
                  <div className="mt-1 text-sm">{text.noDayEventsText}</div>
                </div>
              ) : (
                selectedDayEvents.map((event) => (
                  <div key={event.id} className={`rounded-[24px] border p-4 ${theme === "dark" ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="text-lg font-black">{event.title}</div>
                        <div className={`mt-1 flex flex-wrap gap-3 text-sm ${currentTheme.muted}`}>
                          <span className="inline-flex items-center gap-1"><Clock3 className="h-4 w-4" />{event.startTime || text.allDay}{event.endTime ? ` - ${event.endTime}` : ""}</span>
                          {!!event.location && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{event.location}</span>}
                        </div>
                        {(event.projectName || event.clientName) ? (
                          <div className={`mt-2 text-sm ${currentTheme.muted}`}>
                            {[event.projectName, event.clientName].filter(Boolean).join(" · ")}
                          </div>
                        ) : null}
                        {!!event.notes && (
                          <p className={`mt-3 text-sm leading-6 ${currentTheme.muted}`}>{event.notes}</p>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${eventTypeClasses(event.type, theme)}`}>{text[event.type]}</span>
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${eventStatusClasses(event.status, theme)}`}>{text[event.status]}</span>
                        <button type="button" onClick={() => openEditModal(event)} className={`flex h-10 w-10 items-center justify-center rounded-2xl border transition ${theme === "dark" ? "border-slate-800 bg-slate-900 text-slate-100 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleDelete(event.id)} className={`flex h-10 w-10 items-center justify-center rounded-2xl border transition ${theme === "dark" ? "border-rose-500/20 bg-rose-500/10 text-rose-300 hover:bg-rose-500/15" : "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </PanelCard>
        </div>
      </div>

      <EventModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        form={form}
        setForm={setForm}
        projects={projects}
        editingId={editingEvent?.id}
        text={text}
        theme={theme}
      />
    </PageShell>
  );
}
